import React from 'react';
import { Camera, Download, Eye, EyeOff, FileText, Filter, Image as ImageIcon, Mic, MoreVertical, PlusCircle, Sparkles, Upload } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../ui/dropdown-menu';

const ProductsToolbar = ({ aiSuggestionsLoading, autoLinkImages, autoLinkLoading, categories, downloadTemplate, fetchAISuggestions, handleExport, isRecording, openBatchAIModal, openModal, processVoiceCommand, products, selectedCategoryFilter, setIsImportModalOpen, setSelectedCategoryFilter, setShowMenuAIModal, setShowProductImages, setVoiceCommand, showProductImages, startRecording, stopRecording, voiceCommand, voiceCommandLoading }) => {
  return (
      <div className="flex flex-col gap-4">
        {/* Barra de Comando de Voz/Texto */}
        <div className="bg-gradient-to-r from-gray-800/80 to-gray-900/80 border border-gray-700/50 rounded-xl p-3">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={voiceCommand}
                onChange={(e) => setVoiceCommand(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && processVoiceCommand()}
                placeholder='Comando rápido: "X-Bacon por 25 reais" ou "Desativa a pizza calabresa"'
                className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20"
                data-testid="voice-command-input"
              />
              {voiceCommandLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            <button
              onClick={() => isRecording ? stopRecording() : startRecording()}
              disabled={voiceCommandLoading}
              className={`p-2.5 rounded-lg transition-all ${
                isRecording 
                  ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
              title={isRecording ? "Parar gravação" : "Gravar comando de voz"}
              data-testid="voice-record-button"
            >
              <Mic size={18} className="text-white" />
            </button>
            <button
              onClick={() => processVoiceCommand()}
              disabled={voiceCommandLoading || !voiceCommand.trim()}
              className="px-4 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              data-testid="voice-command-submit"
            >
              <Sparkles size={16} />
              <span className="hidden sm:inline">Executar</span>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
            <Sparkles size={12} className="text-purple-400" />
            IA: Altere preços, ative/desative produtos ou crie novos usando linguagem natural
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Produtos</h1>
              <p className="text-sm text-gray-400 mt-1">
                {products.length} {products.length === 1 ? 'produto' : 'produtos'} no cardápio
              </p>
            </div>
            {/* Toggle para mostrar/ocultar imagens */}
            <button
              onClick={() => setShowProductImages(!showProductImages)}
              className="p-2 bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700 rounded-lg transition-all group"
              title={showProductImages ? "Ocultar imagens (modo compacto)" : "Mostrar imagens"}
              data-testid="toggle-product-images-list"
            >
              {showProductImages ? (
                <Eye className="w-5 h-5 text-blue-400 group-hover:text-blue-300 transition-colors" />
              ) : (
                <EyeOff className="w-5 h-5 text-gray-400 group-hover:text-gray-300 transition-colors" />
              )}
            </button>
            
            {/* Dropdown de Filtro por Categoria - Discreto e Elegante */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-2 text-gray-500 hover:text-gray-300 hover:bg-gray-800/50 rounded-lg transition-colors relative"
                  title="Filtrar por categoria"
                  data-testid="filter-dropdown-button"
                >
                  <Filter size={20} />
                  {selectedCategoryFilter !== 'all' && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-gray-900 border-gray-700 w-56">
                <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase">
                  Filtrar por Categoria
                </div>
                <DropdownMenuItem 
                  onClick={() => setSelectedCategoryFilter('all')}
                  className={`text-gray-300 hover:bg-gray-800 cursor-pointer ${
                    selectedCategoryFilter === 'all' ? 'bg-gray-800/50' : ''
                  }`}
                  data-testid="filter-all-dropdown"
                >
                  <Filter size={16} className="mr-2" />
                  Todos os Produtos
                  <span className="ml-auto text-xs text-gray-500">({products.length})</span>
                </DropdownMenuItem>
                
                {categories.length > 0 && (
                  <>
                    <div className="h-px bg-gray-700 my-1"></div>
                    {categories.map((category) => (
                      <DropdownMenuItem 
                        key={category.id}
                        onClick={() => setSelectedCategoryFilter(category.id)}
                        className={`text-gray-300 hover:bg-gray-800 cursor-pointer ${
                          selectedCategoryFilter === category.id ? 'bg-gray-800/50' : ''
                        }`}
                        data-testid={`filter-category-${category.id}`}
                      >
                        <div 
                          className="w-3 h-3 rounded-full mr-2" 
                          style={{ backgroundColor: category.color }}
                        ></div>
                        {category.name}
                        <span className="ml-auto text-xs text-gray-500">
                          ({products.filter(p => p.category_id === category.id).length})
                        </span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                
                {categories.length === 0 && (
                  <>
                    <div className="h-px bg-gray-700 my-1"></div>
                    <div className="px-3 py-2 text-sm text-gray-500 italic">
                      Nenhuma categoria criada
                    </div>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <button 
            onClick={openModal} 
            className="glass-button-primary w-full sm:w-auto py-3 px-6 text-base font-medium"
            data-testid="new-product-button"
          >
            <PlusCircle size={20} className="mr-2" />
            Novo Produto
          </button>
        </div>
        
        {/* Import/Export Menu - Optimized */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-400 hover:text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
              data-testid="import-export-menu-button"
              title="Importar/Exportar"
            >
              <MoreVertical size={18} />
              <span className="text-sm font-medium">Opções</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-gray-900 border-gray-700 w-56">
            <DropdownMenuItem 
              onClick={() => handleExport('csv')}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="export-csv-button"
            >
              <Download size={16} className="mr-2" />
              Exportar como CSV
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => handleExport('xlsx')}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="export-xlsx-button"
            >
              <Download size={16} className="mr-2" />
              Exportar como XLSX
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setIsImportModalOpen(true)}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="import-button"
            >
              <Upload size={16} className="mr-2" />
              Importar CSV/XLSX
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={downloadTemplate}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="template-button"
            >
              <FileText size={16} className="mr-2" />
              Baixar Template
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-gray-700" />
            <DropdownMenuItem 
              onClick={fetchAISuggestions}
              disabled={aiSuggestionsLoading}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="ai-suggestions-button"
            >
              {aiSuggestionsLoading ? (
                <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Sparkles size={16} className="mr-2 text-purple-400" />
              )}
              Vincular com IA (sugestões)
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={autoLinkImages}
              disabled={autoLinkLoading}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="auto-link-images-button"
            >
              {autoLinkLoading ? (
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <ImageIcon size={16} className="mr-2" />
              )}
              Vincular automático (rápido)
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={openBatchAIModal}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="batch-ai-images-button"
            >
              <Sparkles size={16} className="mr-2 text-purple-400" />
              Gerar Imagens com IA (lote)
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-gray-700" />
            <DropdownMenuItem 
              onClick={() => setShowMenuAIModal(true)}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="menu-ai-button"
            >
              <Camera size={16} className="mr-2 text-green-400" />
              Montar Cardápio com IA
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
  );
};

export default ProductsToolbar;
